import * as Yup from "yup";
import { ALL_DOTS_REG_PATTERN } from "../../strings";
import { validateEmbedUrl } from "../../../services/common-service";
import { findLegalPageWithoutContent } from "../../../services/website-legal-pages";

const SIX_DIGIT_HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const ColorValidation = (what = "") =>
  Yup.string()
    .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid color format")
    .required(what ? `${what} color is required` : "Color is required");
const SocialLinksValidation = Yup.object().shape({
  link: Yup.string(),
  color: ColorValidation()
});

export const websiteSchema = (logoTypes) => {
  const imageSchema = Yup.object().shape({
    key: Yup.string().required("Required"),
    url: Yup.string().required("Required"),
    _id: Yup.string().required("Required"),
    asset: Yup.string().required("Required")
  });

  const linksSchema = Yup.object().shape({
    text: Yup.string().required("Required"),
    link: Yup.object().shape({
      name: Yup.string().required("Required"),
      component: Yup.string().required("Required")
    })
  });

  const brandColorsSchema = Yup.object().shape({
    logo_color: ColorValidation(),
    heading_dark: ColorValidation(),
    heading_light: ColorValidation(),
    text_primary: ColorValidation(),
    text_secondary: ColorValidation(),
    text_light: ColorValidation(),
    btn_primary: ColorValidation(),
    btn_bg_primary: ColorValidation(),
    btn_secondary: ColorValidation(),
    btn_bg_secondary: ColorValidation(),
    primary_bg: ColorValidation(),
    secondary_bg: ColorValidation()
  });

  const headerSchema = Yup.object().shape({
    bgColorDisplay: Yup.boolean(),
    bgColor: Yup.string()
      .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid color format")
      .when("bgColorDisplay", {
        is: true,
        then: (schema) => schema.required("Background color is required"),
        otherwise: (schema) => schema.notRequired()
      }),
    logo: Yup.object().shape({
      type: Yup.object().shape({
        name: Yup.string().required("Type is required"),
        _id: Yup.string().required("Type is required")
      }),
      text: Yup.string().when("type", {
        is: (val) => val?._id === logoTypes[0]?._id,
        then: (schema) => schema.required("Logo text is required"),
        otherwise: (schema) => schema.notRequired()
      }),
      color: Yup.string().when("type", {
        is: (val) => val?._id === logoTypes[0]?._id,
        then: () => ColorValidation("Logo"),
        otherwise: (schema) => schema.notRequired()
      }),
      image: Yup.mixed().when("type", {
        is: (val) => val?._id === logoTypes[1]?._id,
        then: (schema) => schema.required("Logo image is required"),
        otherwise: (schema) => schema.notRequired()
      })
    }),
    links: Yup.object().shape({
      links: Yup.array()
        .of(linksSchema)
        .min(4, "At least 4 links are required")
        .max(4, "Only 4 links are allowed")
        .required("Links are required"),
      color: ColorValidation("Links")
    }),
    phone: Yup.object().shape({
      text: Yup.string().required("Text is required"),
      color: ColorValidation()
    }),
    address: Yup.object().shape({
      text: Yup.string().required("Text is required"),
      color: ColorValidation()
    }),
    secondPhone: Yup.object().shape({
      switchEnabled: Yup.boolean(),
      text: Yup.string().when("switchEnabled", {
        is: true,
        then: (schema) => schema.required("Text is required"),
        otherwise: (schema) => schema.notRequired()
      })
    }),
    secondAddress: Yup.object().shape({
      switchEnabled: Yup.boolean(),
      text: Yup.string().when("switchEnabled", {
        is: true,
        then: (schema) => schema.required("Text is required"),
        otherwise: (schema) => schema.notRequired()
      })
    })
  });

  const heroSectionSchema = Yup.object().shape({
    bgColorDisplay: Yup.boolean(),
    bgColor: Yup.string()
      .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid color format")
      .when("bgColorDisplay", {
        is: true,
        then: (schema) => schema.required("Background color is required"),
        otherwise: (schema) => schema.nullable()
      }),
    heading: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      color: ColorValidation()
    }),
    para: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      color: ColorValidation()
    }),
    bullets: Yup.object().shape({
      data: Yup.array().of(Yup.string().required("Each bullet is required")),
      // .length(3, "Exactly 3 bullets are required"),
      color: ColorValidation()
    }),
    youtubeVideoSwitchEnabled: Yup.boolean(),
    image: Yup.mixed().when("youtubeVideoSwitchEnabled", {
      is: false,
      then: (schema) =>
        schema.test("required", "Image is required", (value) => {
          if (!value || typeof value !== "object") return false;
          // Check if one of the key fields (like 'url' or 'key') exists and is non-empty
          return Boolean(value.url || value.key || value._id || value.asset);
        }),
      otherwise: (schema) => schema.notRequired()
    }),
    youtubeVideoLink: Yup.string().when("youtubeVideoSwitchEnabled", {
      is: true,
      then: (schema) =>
        schema
          .required("Valid link is required")
          .nullable()
          .test("is-embed-link", "Invalid YouTube or Wistia link.", (value) =>
            validateEmbedUrl(value)
          ),
      otherwise: (schema) => schema.notRequired()
    }),
    btn: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      link: Yup.string().required("This Field is Required"),
      color: ColorValidation(),
      bgColor: ColorValidation("Background"),
      bgColorDisplay: Yup.boolean()
    })
  });

  const aboutSchema = Yup.object().shape({
    mobileSortDirection: Yup.boolean().default(false),
    bgColorDisplay: Yup.boolean(),
    bgColor: Yup.string()
      .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid color format")
      .when("bgColorDisplay", {
        is: true,
        then: (schema) => schema.required("Background color is required"),
        otherwise: (schema) => schema.nullable()
      }),
    heading: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      color: ColorValidation("Heading")
    }),
    para: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      color: ColorValidation("Paragraph")
    })
    // images: Yup.array()
    //   .of(imageSchema)
    //   .required("Images are required")
    //   .min(3, "At least 3 images are required")
  });

  const singleServiceSchema = Yup.object().shape({
    heading: Yup.object().shape({
      text: Yup.string().required("Heading is required"),
      color: ColorValidation("Heading")
    }),
    para: Yup.object().shape({
      text: Yup.string().required("Paragraph is required"),
      color: ColorValidation("Paragraph")
    }),
    image: imageSchema
  });

  const serviceSchema = Yup.object().shape({
    bgColorDisplay: Yup.boolean(),
    bgColor: Yup.string()
      .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid color format")
      .when("bgColorDisplay", {
        is: true,
        then: (schema) => schema.required("Background color is required"),
        otherwise: (schema) => schema.nullable()
      }),
    heading: Yup.object().shape({
      text: Yup.string().required("Heading is required"),
      color: ColorValidation("Heading")
    }),
    para: Yup.object().shape({
      text: Yup.string().required("Paragraph is required"),
      color: ColorValidation("Paragraph")
    }),
    services: Yup.array()
      .of(singleServiceSchema)
      .min(3, "At least 3 services are required")
      // .max(3, "Only 3 services are allowed")
      .required("Services are required")
  });

  const bannerSchema = Yup.object().shape({
    heading: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      color: ColorValidation("Heading")
    }),
    para: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      color: ColorValidation("Paragraph")
    }),
    btn: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      link: Yup.string().required("This Field is Required"),
      color: ColorValidation(),
      bgColor: ColorValidation("Background"),
      bgColorDisplay: Yup.boolean()
    }),
    image: imageSchema
  });

  const singleKeypointSchema = Yup.object().shape({
    heading: Yup.object().shape({
      text: Yup.string().required("Heading is required"),
      color: ColorValidation("Heading")
    }),
    para: Yup.object().shape({
      text: Yup.string().required("Paragraph is required"),
      color: ColorValidation("Paragraph")
    })
  });

  const keyPointSchema = Yup.object().shape({
    bgColorDisplay: Yup.boolean(),
    bgColor: Yup.string()
      .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid color format")
      .when("bgColorDisplay", {
        is: true,
        then: (schema) => schema.required("Background color is required"),
        otherwise: (schema) => schema.nullable()
      }),
    heading: Yup.object().shape({
      text: Yup.string().required("Heading is required"),
      color: ColorValidation("Heading")
    }),
    para: Yup.object().shape({
      text: Yup.string().required("Paragraph is required"),
      color: ColorValidation("Paragraph")
    }),
    keyPoints: Yup.array()
      .of(singleKeypointSchema)
      .min(4, "At least 4 key points are required")
      // .max(4, "Only 4 key points are allowed")
      .required("KeyPoints are required")
  });

  const secondBannerSchema = Yup.object().shape({
    bgColorDisplay: Yup.boolean(),
    bgColor: Yup.string()
      .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid color format")
      .when("bgColorDisplay", {
        is: true,
        then: (schema) => schema.required("Background color is required"),
        otherwise: (schema) => schema.nullable()
      }),
    heading: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      color: ColorValidation("Heading")
    }),
    para: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      color: ColorValidation()
    }),
    btn: Yup.object().shape({
      text: Yup.string().required("This Field is Required"),
      link: Yup.string().required("This Field is Required"),
      color: ColorValidation(),
      bgColor: ColorValidation("Background"),
      bgColorDisplay: Yup.boolean()
    }),
    // image: imageSchema
    youtubeVideoSwitchEnabled: Yup.boolean(),
    image: Yup.mixed().when("youtubeVideoSwitchEnabled", {
      is: false,
      then: (schema) =>
        schema.test("required", "Image is required", (value) => {
          if (!value || typeof value !== "object") return false;
          // Check if one of the key fields (like 'url' or 'key') exists and is non-empty
          return Boolean(value.url || value.key || value._id || value.asset);
        }),
      otherwise: (schema) => schema.notRequired()
    }),
    youtubeVideoLink: Yup.string().when("youtubeVideoSwitchEnabled", {
      is: true,
      then: (schema) =>
        schema
          .required("Valid link is required")
          .nullable()
          .test("is-embed-link", "Invalid YouTube or Wistia link.", (value) =>
            validateEmbedUrl(value)
          ),
      otherwise: (schema) => schema.notRequired()
    })
  });

  const mapSchema = Yup.object().shape({
    link: Yup.string()
  });

  const singleFaqSchema = Yup.object().shape({
    q: Yup.string().required("Question is required"),
    a: Yup.string().required("Answer is required")
  });

  const faqSchema = Yup.object().shape({
    bgColorDisplay: Yup.boolean(),
    bgColor: Yup.string()
      .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid color format")
      .when("bgColorDisplay", {
        is: true,
        then: (schema) => schema.required("Background color is required"),
        otherwise: (schema) => schema.nullable()
      }),
    active: Yup.boolean(),
    heading: Yup.object().when("active", {
      is: true,
      then: (schema) =>
        schema.shape({
          text: Yup.string().required("This Field is Required"),
          color: ColorValidation("Heading")
        }),
      otherwise: (schema) =>
        schema.shape({
          text: Yup.string().notRequired(),
          color: Yup.string().notRequired()
        })
    }),
    para: Yup.object().when("active", {
      is: true,
      then: (schema) =>
        schema.shape({
          text: Yup.string().required("This Field is Required"),
          color: ColorValidation("Paragraph")
        }),
      otherwise: (schema) =>
        schema.shape({
          text: Yup.string().notRequired(),
          color: Yup.string().notRequired()
        })
    }),
    faqs: Yup.object().when("active", {
      is: true,
      then: (schema) =>
        schema.shape({
          data: Yup.array()
            .of(singleFaqSchema)
            .min(1, "At least one FAQ is required")
            .max(10, "Maximum 10 faqs are allowed"),
          q: Yup.object().shape({
            color: ColorValidation()
          }),
          a: Yup.object().shape({
            color: ColorValidation()
          })
        }),
      otherwise: (schema) =>
        schema.shape({
          data: Yup.array().notRequired(),
          q: Yup.object().shape({
            color: Yup.string().notRequired()
          }),
          a: Yup.object().shape({
            color: Yup.string().notRequired()
          })
        })
    })
  });

  const footerSchema = Yup.object().shape({
    bgColorDisplay: Yup.boolean(),
    bgColor: Yup.string()
      .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid color format")
      .when("bgColorDisplay", {
        is: true,
        then: (schema) => schema.required("Background color is required"),
        otherwise: (schema) => schema.nullable()
      }),
    logo: Yup.object().shape({
      type: Yup.object().shape({
        name: Yup.string().required("Type is required"),
        _id: Yup.string().required("Type is required")
      }),
      text: Yup.string().when("type._id", {
        is: (id) => id === logoTypes[0]?._id,
        then: (schema) => schema.required("Text logo content is required"),
        otherwise: (schema) => schema.notRequired()
      }),
      color: Yup.string().when("type._id", {
        is: (id) => id === logoTypes[0]?._id,
        then: (schema) =>
          schema
            .matches(SIX_DIGIT_HEX_COLOR_REGEX, "Invalid hex color format")
            .required("Text logo color is required"),
        otherwise: (schema) => schema.notRequired()
      }),
      image: Yup.mixed().when("type._id", {
        is: (id) => id === logoTypes[1]?._id,
        then: (schema) => schema.required("Logo image is required"),
        otherwise: (schema) => schema.notRequired()
      })
    }),
    tag_line: Yup.object().shape({
      text: Yup.string().required("Text is required"),
      color: ColorValidation()
    }),
    // Social Links --- starts
    facebook: SocialLinksValidation,
    instagram: SocialLinksValidation,
    youtube: SocialLinksValidation,
    x_twitter: SocialLinksValidation,
    pinterest: SocialLinksValidation,
    tiktok: SocialLinksValidation,
    linkedin: SocialLinksValidation,
    // Social Links --- ends
    footer_links: Yup.object().shape({
      quick_links: Yup.object().shape({
        links: Yup.array()
          .of(linksSchema)
          .min(4, "At least 4 links are required")
          .max(4, "Only 4 links are allowed")
          .required("Links are required"),
        text: Yup.string().required("Required"),
        color: ColorValidation("Links")
      }),
      agbLinkEnabled: Yup.boolean(),
      pp_links: Yup.object().shape({
        links: Yup.array()
          .min(2, "At least 2 links are required")
          .max(3, "Only 3 links are allowed")
          .required("Links are required")
          .of(
            Yup.object({
              text: Yup.string().nullable()
            })
          )
          .test("conditional-text-required", "Required", function (links) {
            const { agbLinkEnabled } = this.parent;
            if (!Array.isArray(links)) {
              return false;
            }
            const createRequiredError = (_index) =>
              this.createError({
                path: `footer.footer_links.pp_links.links[${_index}].text`,
                message: "Required"
              });
            // AGB enabled (all required)
            if (agbLinkEnabled) {
              const invalidIndex = links.findIndex((l) => !l?.text?.trim());
              if (invalidIndex !== -1) {
                return createRequiredError(invalidIndex);
              }
            } else {
              // AGB disabled (first two required)
              for (let i = 0; i < 2; i++) {
                if (!links[i]?.text?.trim()) {
                  return createRequiredError(i);
                }
              }
            }
            return true;
          })
          .test(
            "legal-page-content-required",
            "customerWebsite.legalPageContentMissing",
            function (links) {
              const index = findLegalPageWithoutContent({
                ...this.parent,
                links
              });
              return index === -1
                ? true
                : this.createError({
                    path: `footer.footer_links.pp_links.links[${index}].content`,
                    message: "customerWebsite.legalPageContentMissing"
                  });
            }
          ),
        showAsPages: Yup.boolean(),
        text: Yup.string().required("Required"),
        color: ColorValidation("Links")
      })
    }),
    contact_card: Yup.object().shape({
      text: Yup.string().required("Required"),
      name: Yup.object().shape({
        text: Yup.string().required("This Field is Required"),
        color: ColorValidation()
      }),
      title: Yup.object().shape({
        text: Yup.string().required("This Field is Required"),
        color: ColorValidation()
      }),
      email: Yup.object().shape({
        text: Yup.string().email("Invalid Email"),
        color: ColorValidation()
      }),
      btn: Yup.object().shape({
        text: Yup.string().required("This Field is Required"),
        link: Yup.string().required("This Field is Required"),
        color: ColorValidation(),
        bgColor: ColorValidation()
      }),
      optionalCtaEnabled: Yup.boolean(),
      optionalBtn: Yup.object().when("optionalCtaEnabled", {
        is: true,
        then: (schema) =>
          schema.shape({
            text: Yup.string().required("This Field is Required"),
            link: Yup.string().required("This Field is Required"),
            color: ColorValidation(),
            bgColor: ColorValidation()
          }),
        otherwise: (schema) =>
          schema.shape({
            text: Yup.string().notRequired(),
            link: Yup.string().notRequired(),
            color: Yup.string().notRequired(),
            bgColor: Yup.string().notRequired()
          })
      }),
      image: Yup.mixed().required("Image is required")
    }),
    local_seo_links: Yup.object().shape({
      color: ColorValidation()
    })
  });

  const sectionValidationSchema = {
    hero: heroSectionSchema,
    about: aboutSchema,
    service: serviceSchema,
    banner: bannerSchema,
    keyPoint: keyPointSchema,
    secondBanner: secondBannerSchema,
    map: mapSchema,
    faq: faqSchema
  };

  const schema = Yup.object().shape({
    domain: Yup.string()
      .required("Domain is required")
      .matches(
        /^(?!https?:\/\/)/,
        "Protocol not allowed. Remove http:// or https://"
      )
      .matches(/^(?!www\.)/, 'Remove "www." from the domain')
      .matches(
        /^[a-z0-9äöüß.-]+$/u,
        "Only lowercase letters, numbers, dot, and hyphen are allowed"
      )
      .matches(
        /^[^!@#$%^&*()_+={}[\]:;"'<>,?/|\\]+$/,
        "Special characters are not allowed"
      )
      .matches(/^(?!.*\.\.)/, "Consecutive dots are not allowed")
      .test("two-dots", "Only two dots are allowed", (value) =>
        value
          ? (value.match(ALL_DOTS_REG_PATTERN) || []).length === 1 ||
            (value.match(ALL_DOTS_REG_PATTERN) || []).length === 2
          : false
      )
      .matches(/^[^.].*[^.]$/, "Dot cannot be at the start or end")
      .max(63, "Domain must be at most 63 characters long"),
    // subDomain: Yup.string().required("Required"),
    language: Yup.string().trim().required("Language is required"),
    brand_colors: brandColorsSchema,
    navbar: headerSchema,
    sections: Yup.array()
      .of(
        Yup.object().shape({
          component: Yup.string()
            .oneOf(
              Object.keys(sectionValidationSchema),
              "Invalid section component"
            )
            .required("Section component is required"),
          data: Yup.lazy((value, options) => {
            const sectionKey = options?.parent?.component;
            const validationSchema = sectionValidationSchema[sectionKey];
            if (validationSchema) {
              return validationSchema;
            }
            return Yup.object();
          })
        })
      )
      .min(1, "At least one section is required"),
    footer: footerSchema
  });

  return schema;
};

export default websiteSchema;
